import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { listIntegrations, CATEGORY_ORDER } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { mapApiConnectedToUi, catalogCategoryLabel } from '../../utils';
import { getCategoryColorClass } from '../../constants';
import { useAbortableLoad } from '../useAbortableLoad.js';

/**
 * Already Integrated data (CT-4160 + CT-4162): loads connected integrations once
 * and derives category groups client-side. Search filtering (by name) and
 * empty-category collapse are a pure `useMemo` over `searchQuery` — no refetch
 * per keystroke (the mock list is already in memory).
 *
 * @param {string} searchQuery - live query from IntegrationsContext (CT-4157 hero)
 * @returns {{ groups, totalMatches, totalConnected, isLoading, error, refetch }}
 */
export function useAlreadyIntegrated(searchQuery = '') {
  const { t } = useTranslation('admin-studio');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const run = useAbortableLoad();

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    await run(async (isStale) => {
      try {
        // `listIntegrations` paginates (default page_size 50); grouping/search/
        // totalConnected need the FULL connected set, so page through all of it
        // instead of silently treating page 1 as the whole list.
        const rows = [];
        let page = 1;
        let totalPages = 1;
        do {
          const res = await listIntegrations({ filters: { connected: true }, page, page_size: 100 });
          if (isStale()) return;
          const normalized = validateApiResponse(res, t('common.requestFailed'));
          rows.push(...(Array.isArray(normalized.body) ? normalized.body : []));
          totalPages = normalized.meta?.total_pages ?? 1;
          page += 1;
        } while (page <= totalPages);
        setItems(rows.map(mapApiConnectedToUi));
      } catch (err) {
        if (isStale()) return;
        setError(err?.message ?? t('common.requestFailed'));
        setItems([]);
      } finally {
        if (!isStale()) setIsLoading(false);
      }
    });
  }, [t, run]);

  useEffect(() => {
    load();
  }, [load]);

  const { groups, totalMatches } = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matched = q ? items.filter((i) => (i.name ?? '').toLowerCase().includes(q)) : items;

    const byCategory = new Map();
    for (const item of matched) {
      if (!byCategory.has(item.category)) byCategory.set(item.category, []);
      byCategory.get(item.category).push(item);
    }

    const ordered = CATEGORY_ORDER.filter((cat) => byCategory.has(cat)).map((cat) => ({
      categoryId: cat,
      label: catalogCategoryLabel(cat, t),
      colorClass: getCategoryColorClass(cat),
      count: byCategory.get(cat).length,
      items: byCategory.get(cat),
    }));

    return { groups: ordered, totalMatches: matched.length };
  }, [items, searchQuery, t]);

  return useMemo(
    () => ({ groups, totalMatches, totalConnected: items.length, isLoading, error, refetch: load }),
    [groups, totalMatches, items.length, isLoading, error, load],
  );
}
